import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, Spinner } from "@/components/Kit";
import { playSfx } from "@/lib/sound";
import * as Icons from "lucide-react";
import { Store, Coins, Package, Search, Tag, PackagePlus, Undo2, ShoppingCart } from "lucide-react";

function toPascal(s){return (s||"").split("-").map(w=>w.charAt(0).toUpperCase()+w.slice(1)).join("");}
const RARITY = { Junk:"#8892a6", Common:"#8892a6", Uncommon:"#2D9CFF", Rare:"#B14DFF", "Ultra-Rare":"#FF2D55", Legendary:"#FFC531" };

function ItemIcon({ icon, rarity, size = 40 }) {
  const Icon = Icons[toPascal(icon)] || Package; const rc = RARITY[rarity] || "#8892a6";
  return <span className="rounded-[var(--radius)] grid place-items-center shrink-0" style={{ width: size, height: size, background: `${rc}22`, color: rc }}><Icon style={{ width: size*0.5, height: size*0.5 }} /></span>;
}

export default function Market() {
  const { user, patchUser } = useOutletContext();
  const [tab, setTab] = useState("state");
  const [market, setMarket] = useState(null);
  const [inv, setInv] = useState([]);
  const [stall, setStall] = useState([]);
  const [stalls, setStalls] = useState([]);
  const [q, setQ] = useState("");
  const [prices, setPrices] = useState({});
  const [busy, setBusy] = useState(null);

  const loadState = () => Promise.all([api.get("/market"), api.get("/inventory")]).then(([m, i]) => { setMarket(m.data.items); setInv(i.data.items); });
  const loadStall = () => Promise.all([api.get("/stall"), api.get("/inventory")]).then(([s, i]) => { setStall(s.data.listings); setInv(i.data.items); });
  const loadStalls = (query = "") => api.get(`/stalls?q=${encodeURIComponent(query)}`).then((r) => setStalls(r.data.listings));
  useEffect(() => { loadState(); loadStall(); loadStalls(); }, []);
  if (!market) return <PageLoader label="Opening the Market" />;

  const buyState = async (it) => {
    setBusy(it.id);
    try { const { data } = await api.post("/shop/buy", { pet_id: it.id }); patchUser({ credits: data.credits }); playSfx("coin"); toast.success(`Bought ${it.name}`); await loadState(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const sellState = async (it) => {
    setBusy(it.id);
    try { const { data } = await api.post("/market/sell", { pet_id: it.id }); patchUser({ credits: data.credits }); playSfx("coin"); toast.success(`Sold ${it.name} for ${data.gained}c`); await loadState(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const listStall = async (it) => {
    const price = parseInt(prices[it.id], 10);
    if (!price || price < 1) { toast.error("Set a price first."); return; }
    setBusy(it.id);
    try { await api.post("/stall/list", { item_id: it.id, price }); playSfx("pop"); toast.success(`${it.name} listed at ${price}c`); setPrices((p) => ({ ...p, [it.id]: "" })); await loadStall(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const unlist = async (l) => {
    setBusy(l.listing_id);
    try { await api.post(`/stall/${l.listing_id}/unlist`, {}); toast.success("Delisted, item returned."); await loadStall(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const buyStall = async (l) => {
    setBusy(l.listing_id);
    try { const { data } = await api.post(`/stalls/${l.listing_id}/buy`, {}); patchUser({ credits: data.credits }); playSfx("coin"); toast.success(`Bought ${l.item.name}`); await loadStalls(q); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  const sellable = inv.filter((i) => i.category !== "scrap");

  return (
    <div data-testid="market-page">
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div>
          <span className="label-cap text-primary flex items-center gap-2"><Store className="w-4 h-4" /> Station Market // Exchange</span>
          <h1 className="text-4xl font-black tracking-tighter mt-2 font-head">Market</h1>
        </div>
        <div className="flex items-center gap-2 mono text-secondary font-bold" data-testid="market-credits"><Coins className="w-5 h-5" /> {user.credits}</div>
      </div>

      <div className="flex gap-1 mb-5 p-1 rounded-[var(--radius)] bg-muted w-fit flex-wrap">
        {[["state", "State Market"], ["mine", `My Stall (${stall.length})`], ["browse", "Browse Stalls"]].map(([id, l]) => (
          <button key={id} data-testid={`market-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono ${tab===id?"bg-primary text-primary-foreground":"text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "state" && (
        <>
          <p className="text-sm text-muted-foreground mb-4">The state always buys at list price and pays 50% when you sell. Guaranteed liquidity, no waiting.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {market.map((it) => {
              const owned = inv.find((x) => x.id === it.id)?.qty || 0;
              const afford = (user.credits || 0) >= it.buy_price;
              return (
                <div key={it.id} data-testid={`market-item-${it.id}`} className="panel p-4 flex flex-col">
                  <div className="flex items-center gap-3 mb-2"><ItemIcon icon={it.icon} rarity={it.rarity} /><div><h3 className="font-head font-bold text-sm">{it.name}</h3><span className="label-cap text-[9px]" style={{ color: RARITY[it.rarity] }}>{it.rarity}{owned?` · own ${owned}`:""}</span></div></div>
                  <p className="text-xs text-muted-foreground flex-1 leading-snug">{it.desc}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => buyState(it)} data-testid={`market-buy-${it.id}`} disabled={!afford || busy===it.id} className={afford?"btn-primary flex-1 text-xs":"btn-ghost flex-1 text-xs opacity-60"}><Coins className="w-3.5 h-3.5" /> {it.buy_price}</button>
                    <button onClick={() => sellState(it)} data-testid={`market-sell-${it.id}`} disabled={!owned || busy===it.id} className={owned?"btn-secondary flex-1 text-xs":"btn-ghost flex-1 text-xs opacity-50"}>Sell {it.sell_price}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "mine" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div>
            <div className="label-cap mb-2 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Your Listings</div>
            <div className="space-y-2">
              {stall.length === 0 && <div className="panel p-6 text-center text-muted-foreground text-sm">Nothing listed. Set prices on the right to open your stall.</div>}
              {stall.map((l) => (
                <div key={l.listing_id} data-testid={`stall-listing-${l.listing_id}`} className="panel p-3 flex items-center gap-3">
                  <ItemIcon icon={l.item.icon} rarity={l.item.rarity} size={36} />
                  <div className="flex-1 min-w-0"><div className="text-sm font-bold truncate">{l.item.name}</div><div className="mono text-xs text-secondary">{l.price}c</div></div>
                  <button onClick={() => unlist(l)} data-testid={`unlist-${l.listing_id}`} disabled={busy===l.listing_id} className="btn-ghost text-xs"><Undo2 className="w-3.5 h-3.5" /> Delist</button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="label-cap mb-2 flex items-center gap-1.5"><PackagePlus className="w-3.5 h-3.5" /> List From Inventory</div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {sellable.length === 0 && <div className="panel p-6 text-center text-muted-foreground text-sm">No listable items.</div>}
              {sellable.map((it) => (
                <div key={it.id} data-testid={`listable-${it.id}`} className="panel p-3 flex items-center gap-2">
                  <ItemIcon icon={it.icon} rarity={it.rarity} size={36} />
                  <div className="flex-1 min-w-0"><div className="text-sm font-bold truncate">{it.name}</div><span className="label-cap text-[9px]">own {it.qty}</span></div>
                  <input data-testid={`price-${it.id}`} type="number" min="1" placeholder="price" value={prices[it.id] || ""} onChange={(e) => setPrices((p) => ({ ...p, [it.id]: e.target.value }))}
                    className="w-20 px-2 py-1.5 rounded-[var(--radius)] bg-muted border border-border mono text-xs focus:outline-none focus:border-primary" />
                  <button onClick={() => listStall(it)} data-testid={`list-${it.id}`} disabled={busy===it.id} className="btn-primary text-xs">List</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "browse" && (
        <>
          <div className="panel p-2 mb-4 flex items-center gap-2">
            <Search className="w-4 h-4 text-muted-foreground ml-1" />
            <input data-testid="stall-search" value={q} onChange={(e) => { setQ(e.target.value); loadStalls(e.target.value); }} placeholder="Search by item or pilot…"
              className="flex-1 bg-transparent text-sm focus:outline-none py-1.5" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stalls.length === 0 && <div className="col-span-full panel p-8 text-center text-muted-foreground">No stalls match. Pilots list gear here — check back soon.</div>}
            {stalls.map((l) => {
              const afford = (user.credits || 0) >= l.price;
              return (
                <div key={l.listing_id} data-testid={`stall-${l.listing_id}`} className="panel p-4 flex flex-col">
                  <div className="flex items-center gap-3 mb-2"><ItemIcon icon={l.item.icon} rarity={l.item.rarity} /><div className="min-w-0"><h3 className="font-head font-bold text-sm truncate">{l.item.name}</h3><span className="label-cap text-[9px]">by {l.seller_name}</span></div></div>
                  <p className="text-xs text-muted-foreground flex-1 leading-snug">{l.item.desc}</p>
                  <button onClick={() => buyStall(l)} data-testid={`stall-buy-${l.listing_id}`} disabled={!afford || busy===l.listing_id} className={afford?"btn-primary w-full mt-3 text-xs":"btn-ghost w-full mt-3 text-xs opacity-60"}>
                    <ShoppingCart className="w-3.5 h-3.5" /> {l.price}c
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
